import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactDOM from "react-dom";
import { Typography, IconButton, Box, Card, CardContent, Avatar, ClickAwayListener } from "@mui/material";
import { motion } from "framer-motion";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import styles from "./FavoriteList.module.css";
import { addFavouration } from "../../features/auth/authAction";
import { alertActions } from "../../features/auth/alertSlice";

const FavoriteListView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showList, setShowList] = useState(false);
  const [position, setPosition] = useState(() => {
    const storedPos = localStorage.getItem('favoriteListPosition');
    if (storedPos) {
      try {
        const parsed = JSON.parse(storedPos);
        if (parsed.x !== undefined && parsed.y !== undefined) {
          return parsed;
        }
      } catch (e) { }
    }
    return { x: window.innerWidth - 80, y: 24 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isClick, setIsClick] = useState(true);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const dragStartedRef = useRef(false);
  const isMouseDownRef = useRef(false);
  const [overlayPosition, setOverlayPosition] = useState({ top: 'auto', bottom: 'auto', left: 'auto', right: 0 });
  const [hasViewedList, setHasViewedList] = useState(() => {
    return localStorage.getItem('hasViewedFavorites') === 'true';
  });

  const favList = useSelector((state) => state.user?.userFavDisplay?.data);
  const loggedUId = useSelector((state) => state.auth?.profileInfo?.user?.id);
  const userDetails = useSelector((state) => state.auth?.profileInfo?.user);
  const paidUserList = useSelector((state) => state.cartDetails?.paidUserList);
  const permission = useSelector(state => state.auth?.permission?.user_permission);

  const handleFav = (favId, favName) => {
    // Check if user has paid
    if (paidUserList?.data?.length > 0) {
      // Check for permission (admin approval)
      if (Number(permission) === 1) {
        dispatch(addFavouration({
          'favId': loggedUId, // Logged user ID
          'favName': userDetails?.name, // Logged user name
          'userId': favId // The user we are favoriting
        })).then((result) => {
          // Optional: You could update local list or show a specific success toast here
        });
      } else {
        dispatch(
          alertActions.createAlert({
            message: 'Permission denied by Admin',
            type: 'warning',
          })
        );
      }
    }
    else {
      navigate(`/payment/${loggedUId}`);
      dispatch(
        alertActions.createAlert({
          message: 'You have to do the payment !!!',
          type: 'warning',
        })
      );
    }
  };

  // Reset hasViewedList when new favorites are added
  useEffect(() => {
    if (!favList) return;

    const currentCount = favList.length;
    const storedCount = parseInt(localStorage.getItem('favoritesCount') || '0', 10);

    // If current count is greater than stored count, new favorites were added
    if (currentCount > storedCount) {
      setHasViewedList(false);
      localStorage.setItem('hasViewedFavorites', 'false');
    }

    // Always update the stored count
    localStorage.setItem('favoritesCount', String(currentCount));
  }, [favList]);

  useEffect(() => {
    const handleResize = () => {
      const containerWidth = containerRef.current?.offsetWidth || 56;
      const containerHeight = containerRef.current?.offsetHeight || 56;
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - containerWidth),
        y: Math.min(prev.y, window.innerHeight - containerHeight)
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const updateOverlayPosition = useCallback(() => {
    if (!containerRef.current || !showList) return;

    const buttonRect = containerRef.current.getBoundingClientRect();
    const overlayWidth = 380;
    const overlayHeight = Math.min(window.innerHeight * 0.7, 500);
    const spacing = 8; // Small gap between icon and popup

    let newTop = 'auto';
    let newBottom = 'auto';
    let newLeft = 'auto';
    let newRight = 'auto';

    // Horizontal: try to place overlay to the right of the icon
    if (buttonRect.right + overlayWidth + spacing <= window.innerWidth) {
      // Enough space on the right
      newLeft = buttonRect.right + spacing;
      newRight = 'auto';
    } else if (buttonRect.left - overlayWidth - spacing >= 0) {
      // Not enough space on right, but enough on left → place to the left
      newLeft = buttonRect.left - overlayWidth - spacing;
      newRight = 'auto';
    } else {
      // Not enough space on either side → center horizontally near icon
      newLeft = Math.max(spacing, Math.min(buttonRect.left, window.innerWidth - overlayWidth - spacing));
      newRight = 'auto';
    }

    // Vertical: place overlay below the icon (aligned to its vertical center)
    const iconCenterY = buttonRect.top + buttonRect.height / 2;
    const overlayTop = iconCenterY - overlayHeight / 2;

    if (overlayTop >= 0 && overlayTop + overlayHeight <= window.innerHeight) {
      // Fits vertically
      newTop = overlayTop;
      newBottom = 'auto';
    } else if (buttonRect.bottom + overlayHeight <= window.innerHeight) {
      // Try below the icon
      newTop = buttonRect.bottom + spacing;
      newBottom = 'auto';
    } else if (buttonRect.top - overlayHeight >= 0) {
      // Try above the icon
      newTop = 'auto';
      newBottom = window.innerHeight - buttonRect.top + spacing;
    } else {
      // Fallback: stick to top or bottom
      newTop = spacing;
      newBottom = 'auto';
    }

    setOverlayPosition({ top: newTop, bottom: newBottom, left: newLeft, right: newRight });
  }, [showList]);

  useEffect(() => {
    updateOverlayPosition();
  }, [position, showList, updateOverlayPosition]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Only process movement if mouse button is pressed down
      if (!isMouseDownRef.current) return;

      // Check if mouse moved significantly (more than 8px) - if so, it's a drag
      const moveDistance = Math.sqrt(
        Math.pow(e.clientX - startPos.x, 2) + Math.pow(e.clientY - startPos.y, 2)
      );

      if (moveDistance > 8 && !dragStartedRef.current) {
        // Start dragging only if mouse moved significantly
        setIsDragging(true);
        setIsClick(false);
        dragStartedRef.current = true;
        e.preventDefault();
      }

      // Once dragging has started, smoothly follow the mouse cursor
      if (dragStartedRef.current) {
        e.preventDefault();
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        const containerWidth = containerRef.current?.offsetWidth || 56;
        const containerHeight = containerRef.current?.offsetHeight || 56;
        const maxX = window.innerWidth - containerWidth;
        const maxY = window.innerHeight - containerHeight;
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      isMouseDownRef.current = false;
      if (dragStartedRef.current) {
        setIsDragging(false);
      }
      // Reset states after a short delay
      setTimeout(() => {
        setIsClick(true);
        dragStartedRef.current = false;
      }, 100);
    };

    // Always set up event listeners - they check isMouseDownRef internally
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [dragOffset, startPos]);

  useEffect(() => {
    localStorage.setItem('favoriteListPosition', JSON.stringify(position));
  }, [position]);

  const handleMouseDown = (e) => {
    // Don't start dragging if clicking on the button itself
    if (e.target.closest('button') || e.target.closest('.heartContainer')) {
      return;
    }
    // Mark that mouse button is pressed down
    isMouseDownRef.current = true;
    // Record start position, but wait for mouse movement to determine if it's a drag
    setIsClick(true);
    dragStartedRef.current = false;
    setStartPos({ x: e.clientX, y: e.clientY });
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleButtonMouseDown = (e) => {
    // Mark that mouse button is pressed down
    isMouseDownRef.current = true;
    // Record the start position but don't start dragging yet
    // Wait to see if mouse moves significantly
    setIsClick(true);
    dragStartedRef.current = false;
    setStartPos({ x: e.clientX, y: e.clientY });
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    // Only toggle if it was a genuine click (not a drag)
    if (isClick && !isDragging) {
      setShowList((prev) => !prev);
      // Mark as viewed when opening the list
      if (!showList) {
        setHasViewedList(true);
        localStorage.setItem('hasViewedFavorites', 'true');
      }
    }
  };

  if (!loggedUId) return null;
  return (
    <div
      ref={containerRef}
      className={styles.favoriteListContainer}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
    >
      <motion.button
        type="button"
        aria-label="Favorites"
        className={styles.heartContainer}
        initial={{ scale: 1 }}
        animate={{ scale: isDragging ? 0.95 : [1, 1.1, 1] }}
        transition={{ duration: 1.2, repeat: Infinity, repeatType: "reverse" }}
        onClick={handleButtonClick}
        onMouseDown={handleButtonMouseDown}
        style={{ border: 'none', background: 'transparent', pointerEvents: 'auto', cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <FavoriteIcon sx={{ fontSize: 28, color: '#e92020ff' }} />
        {!hasViewedList && favList?.length > 0 && (
          <motion.span
            className={styles.notificationBadge}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {favList?.length}
          </motion.span>
        )}
      </motion.button>

      {showList && ReactDOM.createPortal(
        <ClickAwayListener onClickAway={() => setShowList(false)}>
          <div
            className={styles.overlay}
            style={overlayPosition}
          >
            <Box onClick={(e) => e.stopPropagation()}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  mb: 2,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Welcome back, {userDetails?.name || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    ඔබව පිළිගත් අය
                  </Typography>
                </Box>
                <IconButton
                  aria-label="Close"
                  size="small"
                  onClick={() => setShowList(false)}
                  sx={{
                    color: 'text.secondary',
                    flexShrink: 0,
                    mt: -0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(113, 18, 62, 0.1)',
                      color: 'primary.main',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box className={styles.cardsContainer}>
                {favList?.length > 0 ? (
                  favList.map((item) => (
                    <Card
                      key={item.id}
                      className={styles.favoriteCard}
                      component={NavLink}
                      to={`./moreDetailsPage/${loggedUId}/${item.favId}`}
                      sx={{
                        textDecoration: 'none',
                        mb: 1.5,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(113, 18, 62, 0.2)',
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: '#C3937C',
                              width: 48,
                              height: 48,
                              fontSize: '1.25rem',
                              fontWeight: 600,
                            }}
                          >
                            {item.favName?.charAt(0)?.toUpperCase() || <PersonIcon />}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.favName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <FavoriteIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              Added to favorites
                            </Typography>
                          </Box>
                          <IconButton
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFav(item.favId, item.favName);
                            }}
                            sx={{
                              color: 'var(--brand-primary)',
                              '&:hover': {
                                color: '#e92020ff',
                                backgroundColor: 'rgba(233, 32, 32, 0.1)'
                              }
                            }}
                          >
                            <FavoriteBorderOutlinedIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 4,
                      px: 2,
                    }}
                  >
                    <FavoriteBorderOutlinedIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                    />
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                      No favorites available
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      Users who add you to favorites will appear here
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </div>
        </ClickAwayListener>,
        document.body
      )}
    </div>
  );
};

export default FavoriteListView;
